import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { ModelSelectorDropdown } from "src/aiNode/editorComponents/ModelSelectorDropdown";
import {
  AiNodeQuota,
  AiNodeV2Form,
  BasicModelCard,
  RenderableModelCard,
} from "src/aiNode/types";
import { Card } from "src/base-components/Card";
import { FormItem } from "src/base-components/FormItem";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

const MODELS_BEHIND_FEATURE_FLAGS: { [model_id: string]: FEATURE_FLAGS } = {};

const WORK_IN_PROGRESS_MODELS: Array<{ provider: string; model_id: string }> = [
  // OpenAI models
  { provider: "openai", model_id: "gpt-4-1" },
  { provider: "openai", model_id: "gpt-4-1-mini" },
  { provider: "openai", model_id: "gpt-5-mini" },
];

type ModelSelectorProps = {
  models: BasicModelCard[];
  quotas: AiNodeQuota;
  immutable: boolean;
};

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  quotas,
  immutable,
}) => {
  const { setValue, watch, control } = useFormContext<AiNodeV2Form>();
  const selectedModelId = watch("model.model_id");

  const renderableModels = models.reduce((filteredModels, model) => {
    // Only show models behind FF if they are already selected or the FF is enabled.
    if (
      model.model_id != selectedModelId &&
      model.model_id in MODELS_BEHIND_FEATURE_FLAGS &&
      !isFeatureFlagEnabled(MODELS_BEHIND_FEATURE_FLAGS[model.model_id])
    ) {
      return filteredModels;
    }

    // Only show work-in-progress models if they are already selected or the FF is enabled.
    const isWorkInProgressModel = WORK_IN_PROGRESS_MODELS.some(
      (wipModel) => wipModel.model_id === model.model_id,
    );
    if (
      model.model_id != selectedModelId &&
      isWorkInProgressModel &&
      !isFeatureFlagEnabled(FEATURE_FLAGS.aiNodeWorkInProgressModels)
    ) {
      return filteredModels;
    }

    const modelMonthlyTokenQuota = quotas.usage?.[model.model_id]?.token_quota;
    // If no monthly quota is configured (or cannot be found for any reason) the model is not available
    let isModelAvaialble = false;
    if (modelMonthlyTokenQuota !== undefined && modelMonthlyTokenQuota !== 0) {
      // If the monthly quota is not set to zero the model is available
      isModelAvaialble = true;
    }

    filteredModels.push({ ...model, available: isModelAvaialble });
    return filteredModels;
  }, [] as RenderableModelCard[]);

  return (
    <Card className="w-full pb-4 pt-4">
      <FormItem>
        <FormItem.Label
          className="mb-2"
          description="Pick an AI model for your task. Each model has different capabilities and performance characteristics."
          isRequired
        >
          Select AI model
        </FormItem.Label>
        <Controller
          control={control}
          name="model.model_id"
          render={(_) => (
            <div className="w-full">
              <ModelSelectorDropdown
                immutable={immutable}
                models={renderableModels}
                selectedModelId={selectedModelId}
                onSelect={(newModelId) => {
                  const modelCard = renderableModels.find(
                    (model) => model.model_id === newModelId,
                  )!;

                  if (modelCard.available) {
                    setValue("model", {
                      model_id: newModelId,
                      provider: modelCard.providers?.[0] ?? "bedrock",
                    });
                  }
                }}
              />
            </div>
          )}
        />
      </FormItem>
    </Card>
  );
};
